import React, { useMemo, useState } from "react";
import { Plus, X, Search, GripVertical, Sparkles } from "lucide-react";
import { ICONS, ICON_LIST } from "./ProductHighlights";

/**
 * HighlightsPicker
 * -----------------
 * Admin UI for curating the `product.highlights` array (feature-icon row on
 * the product detail page). Lets you:
 *   • add an icon from the full ICON_LIST library (searchable)
 *   • edit the EN + AR label of each highlight (with sensible defaults)
 *   • reorder via up/down buttons
 *   • remove with a single click
 *
 * Value shape: [{ icon: "smart-eye", label: "Intelligent Eye", label_ar: "..." }]
 *
 * Props:
 *   value    — current highlights array
 *   onChange — (newArray) => void
 */
export default function HighlightsPicker({ value = [], onChange }) {
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");

  const chosen = Array.isArray(value) ? value : [];
  const chosenKeys = useMemo(() => new Set(chosen.map((h) => h.icon)), [chosen]);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ICON_LIST.filter((row) => {
      if (chosenKeys.has(row.key)) return false;
      if (!q) return true;
      return (
        row.key.includes(q) ||
        row.en.toLowerCase().includes(q) ||
        row.ar.includes(q)
      );
    });
  }, [search, chosenKeys]);

  const addIcon = (row) => {
    onChange([...chosen, { icon: row.key, label: row.en, label_ar: row.ar }]);
    setSearch("");
    setPicking(false);
  };

  const removeAt = (idx) => {
    onChange(chosen.filter((_, i) => i !== idx));
  };

  const move = (idx, delta) => {
    const next = [...chosen];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const patchAt = (idx, patch) => {
    const next = [...chosen];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-700">
          <Sparkles size={12} className="inline mr-1 text-electric" />
          Feature icons ({chosen.length})
        </div>
        <button
          type="button"
          data-testid="highlights-open-picker"
          onClick={() => setPicking((p) => !p)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-ink text-white hover:bg-electric text-[10px] font-mono uppercase tracking-widest"
        >
          <Plus size={12} /> {picking ? "Close picker" : "Add icon"}
        </button>
      </div>

      {/* Library picker */}
      {picking && (
        <div
          data-testid="highlights-picker-panel"
          className="border border-black/10 rounded-lg p-3 bg-secondary/60"
        >
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search icons (eco, sleep, humidity, wifi…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="highlights-search"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-black/15 text-sm focus:border-electric focus:outline-none"
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2">
            {available.length === 0 && (
              <div className="col-span-full text-[11px] font-mono text-slate-500 py-6 text-center">
                No matches. Try a different term, or you've added them all.
              </div>
            )}
            {available.map((row) => {
              const Icon = ICONS[row.key] || Sparkles;
              return (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => addIcon(row)}
                  data-testid={`highlights-add-${row.key}`}
                  className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white border border-black/10 hover:border-electric hover:shadow-[0_4px_12px_-6px_rgba(0,0,0,0.15)] transition text-left"
                >
                  <div className="shrink-0 w-9 h-9 rounded-md bg-secondary grid place-items-center">
                    <Icon size={18} className="text-electric" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold truncate">{row.en}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate" dir="rtl">
                      {row.ar}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected list */}
      {chosen.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-lg p-4 text-xs text-slate-500 font-mono">
          No feature icons yet — click "Add icon" to choose from the library.
        </div>
      ) : (
        <div className="space-y-2">
          {chosen.map((h, idx) => {
            const Icon = ICONS[h.icon] || Sparkles;
            return (
              <div
                key={`${h.icon}-${idx}`}
                data-testid={`highlight-row-${idx}`}
                className="flex items-center gap-2 p-2 bg-white border border-black/10 rounded-lg"
              >
                <div className="shrink-0 w-10 h-10 rounded-md bg-secondary grid place-items-center">
                  <Icon size={20} className="text-electric" strokeWidth={1.75} />
                </div>
                <input
                  type="text"
                  value={h.label || ""}
                  onChange={(e) => patchAt(idx, { label: e.target.value })}
                  placeholder="Label (EN)"
                  className="flex-1 min-w-0 h-9 px-2 border border-black/10 rounded text-sm focus:border-electric focus:outline-none"
                />
                <input
                  type="text"
                  dir="rtl"
                  value={h.label_ar || ""}
                  onChange={(e) => patchAt(idx, { label_ar: e.target.value })}
                  placeholder="العنوان"
                  className="flex-1 min-w-0 h-9 px-2 border border-black/10 rounded text-sm focus:border-electric focus:outline-none"
                />
                <div className="shrink-0 flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-1 text-slate-500 hover:text-ink disabled:opacity-30"
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === chosen.length - 1}
                    className="p-1 text-slate-500 hover:text-ink disabled:opacity-30"
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="p-1 text-destructive hover:bg-destructive/10 rounded"
                    aria-label="Remove"
                    data-testid={`highlight-remove-${idx}`}
                  >
                    <X size={14} />
                  </button>
                </div>
                <span className="hidden"><GripVertical size={14} /></span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
